import {Checkbox, Select} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import {SyntheticEvent, useCallback} from 'react';

import {Text} from '../../../../../../../../../component/text/text';
import {Spinner} from '../../../../../../../../../layout/spinner/spinner';
import {useLocale} from '../../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../../provider/locale/localization';
import {AiPostTypeEnum, PostGenerationOptionsType} from '../../../../../../../../../service/posts/posts-types';
import {getEnumValue} from '../../../../../../../../../util/enum';

import {AiGenerationSettingsDraftType} from './ai-generation-type';
import * as styles from './generation-settings-step.scss';

const USER_PROMPT_MAX_LENGTH = 1000;

type PropsType = {
    value: AiGenerationSettingsDraftType;
    onChange: (value: AiGenerationSettingsDraftType) => void;
    options: PostGenerationOptionsType | undefined;
    isLoadingOptions: boolean;
};

export function GenerationSettingsStep(props: PropsType): JSX.Element {
    const {value, onChange, options, isLoadingOptions} = props;

    const {getLocalizedString} = useLocale();

    const handleChange = useCallback(
        (partialValue: Partial<AiGenerationSettingsDraftType>) => {
            onChange({...value, ...partialValue});
        },
        [onChange, value]
    );

    if (isLoadingOptions) {
        return <Spinner />;
    }

    return (
        <div className={styles.GenerationSettingsStep}>
            <div className={styles.GenerationSettingsStep_field}>
                <Text block stringKey="POSTS_AI_GENERATION__SETUP__POST_TYPE__LABEL" />

                <Select<AiPostTypeEnum>
                    onChange={(newPostType) => handleChange({postType: newPostType})}
                    placeholder={getLocalizedString('POSTS_AI_GENERATION__SETUP__POST_TYPE__LABEL')}
                    value={value.postType}
                >
                    {(options?.postTypes ?? []).map((postTypeOption) => {
                        const postTypeValue = getEnumValue(AiPostTypeEnum, postTypeOption.key);

                        if (!postTypeValue) {
                            return null;
                        }

                        return (
                            <Select.Option key={postTypeOption.key} value={postTypeValue}>
                                {postTypeOption.label}
                            </Select.Option>
                        );
                    })}
                </Select>
            </div>

            <div className={styles.GenerationSettingsStep_field}>
                <div className={styles.GenerationSettingsStep_label}>
                    <Text block stringKey="POSTS_AI_GENERATION__SETUP__PROMPT__LABEL" />

                    {options?.imageEnabled && (
                        <Text block secondary small stringKey="POSTS_AI_GENERATION__SETUP__PROMPT__HINT" />
                    )}
                </div>

                <TextArea
                    maxLength={USER_PROMPT_MAX_LENGTH}
                    onChange={(event: SyntheticEvent<HTMLTextAreaElement>) => {
                        handleChange({userPrompt: event.currentTarget.value});
                    }}
                    placeholder={getLocalizedString('POSTS_AI_GENERATION__SETUP__PROMPT__PLACEHOLDER')}
                    rows={4}
                    showCount={{
                        formatter: (formatterOptions) =>
                            `${formatterOptions.value.length}/${formatterOptions.maxLength}`,
                    }}
                    value={value.userPrompt}
                />
            </div>

            {options?.imageEnabled && (
                <div className={styles.GenerationSettingsStep_field}>
                    <Checkbox
                        checked={value.generateImage}
                        onChange={(event) => handleChange({generateImage: event.target.checked})}
                    >
                        <Locale stringKey="POSTS_AI_GENERATION__SETUP__GENERATE_IMAGE" />
                    </Checkbox>
                </div>
            )}
        </div>
    );
}
